// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Account from "./account.js"

const modelClassData = {
  "attributes": {
    "account_id": {
      "column": {
        "default": null,
        "name": "account_id",
        "null": false,
        "type": null
      },
      "name": "account_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "vat_number": {
      "column": {
        "default": null,
        "name": "vat_number",
        "null": false,
        "type": "string"
      },
      "name": "vat_number",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "VatNumber",
  "collectionKey": "vat_numbers",
  "collectionName": "vat_numbers",
  "i18nKey": "vat_number",
  "camelizedLower": "vatNumber",
  "name": "VatNumber",
  "nameDasherized": "vat-number",
  "pluralName": "vat_numbers",
  "ransackable_associations": [
    {
      "className": "Account",
      "collectionName": "accounts",
      "foreignKey": "account_id",
      "name": "account",
      "macro": "belongs_to",
      "resource_name": "Account",
      "through": null
    },
    {
      "className": "VatNumber::Audit",
      "collectionName": "vat_number_audits",
      "foreignKey": "vat_number_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "VatNumberAudit",
      "through": null
    },
    {
      "className": "VatNumber::Audit",
      "collectionName": "vat_number_audits",
      "foreignKey": "vat_number_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "VatNumberAudit",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "account_id",
      "column": {
        "default": null,
        "name": "account_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "state",
      "column": {
        "default": "active",
        "name": "state",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "valvat_address",
      "column": {
        "default": null,
        "name": "valvat_address",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "valvat_company_type",
      "column": {
        "default": null,
        "name": "valvat_company_type",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "valvat_country_code",
      "column": {
        "default": null,
        "name": "valvat_country_code",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "valvat_name",
      "column": {
        "default": null,
        "name": "valvat_name",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "valvat_request_date",
      "column": {
        "default": null,
        "name": "valvat_request_date",
        "null": true,
        "type": "date"
      }
    },
    {
      "name": "valvat_request_identifier",
      "column": {
        "default": null,
        "name": "valvat_request_identifier",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "vat_number",
      "column": {
        "default": null,
        "name": "vat_number",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "vat_rate",
      "column": {
        "default": "0",
        "name": "vat_rate",
        "null": true,
        "type": "decimal"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Account",
      "collectionName": "accounts",
      "foreignKey": "account_id",
      "name": "account",
      "macro": "belongs_to",
      "resource_name": "Account",
      "through": null
    }
  ],
  "paramKey": "vat_number",
  "primaryKey": "id"
}

/** Frontend model for VatNumber. */
export default class VatNumber extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  accountId() {
    return this.readAttributeUnderscore("account_id")
  }

  /** @returns {boolean} */
  hasAccountId() {
    const value = this.accountId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  vatNumber() {
    return this.readAttributeUnderscore("vat_number")
  }

  /** @returns {boolean} */
  hasVatNumber() {
    const value = this.vatNumber()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static lookup(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "lookup",
        collectionName: "vat_numbers",
        type: "collection"
      },
      commandArgs
    ))
  }

  /** @returns {import("./account.js").default | null} */
  account() {
    return this._readBelongsToReflection({modelClass: Account, reflectionName: "account"})
  }

  /** @returns {Promise<import("./account.js").default | null>} */
  loadAccount() {
    if (!("accountId" in this)) throw new Error("Foreign key method wasn't defined: accountId")

    const id = this.accountId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "account",
        model: this,
        modelClass: Account
      },
      {ransack}
    )
  }
}
